import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import { Loader2, Play, Image, Globe, Users, Lock, AlertCircle, CheckCircle2, AlertTriangle, Info } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * TikTok Post Modal - FULLY COMPLIANT with TikTok Content Sharing UX Guidelines
 * https://developers.tiktok.com/doc/content-sharing-guidelines
 */
export default function TikTokPostModal({ 
  isOpen, 
  onClose, 
  content,
  userId,
  onPostSuccess 
}) {
  const [loading, setLoading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [creatorInfo, setCreatorInfo] = useState(null);
  const [creatorInfoError, setCreatorInfoError] = useState(null);
  const [canPost, setCanPost] = useState(true);
  const [postingLimitMessage, setPostingLimitMessage] = useState(null);
  const [videoDurationError, setVideoDurationError] = useState(null);
  
  // POINT 2: Post metadata - NO DEFAULTS
  const [caption, setCaption] = useState('');
  const [privacyLevel, setPrivacyLevel] = useState('');
  
  // POINT 2c: Interaction settings - ALL OFF BY DEFAULT
  const [allowComment, setAllowComment] = useState(false);
  const [allowDuet, setAllowDuet] = useState(false);
  const [allowStitch, setAllowStitch] = useState(false);
  
  // Disabled states from creator_info API
  const [commentDisabledByCreator, setCommentDisabledByCreator] = useState(false);
  const [duetDisabledByCreator, setDuetDisabledByCreator] = useState(false);
  const [stitchDisabledByCreator, setStitchDisabledByCreator] = useState(false);
  
  // POINT 3: Content Disclosure Settings
  const [discloseContent, setDiscloseContent] = useState(false);
  const [isYourBrand, setIsYourBrand] = useState(false);
  const [isBrandedContent, setIsBrandedContent] = useState(false);
  
  // POINT 4 & 5: User consent
  const [userConsent, setUserConsent] = useState(false);

  // POINT 1: Query creator_info API FIRST
  useEffect(() => {
    if (isOpen && userId) {
      setPrivacyLevel('');
      setAllowComment(false);
      setAllowDuet(false);
      setAllowStitch(false);
      setDiscloseContent(false);
      setIsYourBrand(false);
      setIsBrandedContent(false);
      setUserConsent(false);
      setCanPost(true);
      setPostingLimitMessage(null);
      setVideoDurationError(null);
      fetchCreatorInfo();
    }
  }, [isOpen, userId]);

  useEffect(() => {
    if (content) {
      const initialCaption = content.caption || content.script || '';
      const hashtags = content.hashtags?.join(' ') || '';
      setCaption(`${initialCaption}\n\n${hashtags}`.trim());
    }
  }, [content]);

  const fetchCreatorInfo = async () => {
    setLoading(true);
    setCreatorInfoError(null);
    
    try {
      const response = await fetch(`${API}/api/tiktok/creator-info?user_id=${userId}`);
      const data = await response.json();
      
      if (data.success) {
        setCreatorInfo(data.creator_info);
        
        if (data.creator_info?.posting_available === false) {
          setCanPost(false);
          setPostingLimitMessage("You've reached your daily posting limit. Please try again later.");
        }
        
        if (content?.video_duration && data.creator_info?.max_video_post_duration_sec) {
          if (content.video_duration > data.creator_info.max_video_post_duration_sec) {
            setCanPost(false);
            setVideoDurationError(
              `Video is ${content.video_duration}s but max allowed is ${data.creator_info.max_video_post_duration_sec}s`
            );
          }
        }
        
        if (data.creator_info?.comment_disabled) setCommentDisabledByCreator(true);
        if (data.creator_info?.duet_disabled) setDuetDisabledByCreator(true);
        if (data.creator_info?.stitch_disabled) setStitchDisabledByCreator(true);
        
      } else {
        setCreatorInfoError(data.error || 'Failed to fetch TikTok account info');
      }
    } catch (error) {
      console.error('Error fetching creator info:', error);
      setCreatorInfoError('Failed to connect to TikTok. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Validate commercial content disclosure
  const isCommercialContentValid = () => {
    if (discloseContent) {
      return isYourBrand || isBrandedContent;
    }
    return true;
  };

  // POINT 3a: Get the SINGLE prompt message based on checkbox selection
  const getDisclosurePrompt = () => {
    if (isBrandedContent) {
      // If Branded Content is checked (alone or with Your Brand), show "Paid partnership"
      return "Your photo/video will be labeled as 'Paid partnership'. This cannot be changed once your video is posted.";
    } else if (isYourBrand) {
      // If only Your Brand is checked, show "Promotional content"
      return "Your photo/video will be labeled as 'Promotional content'. This cannot be changed once your video is posted.";
    }
    return null;
  };

  // POINT 4: Get consent text based on selections
  const getConsentText = () => {
    if (isBrandedContent) {
      return (
        <>
          By posting, you agree to TikTok&apos;s{' '}
          <a href="https://www.tiktok.com/legal/page/global/bc-policy" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:underline">
            Branded Content Policy
          </a>{' '}
          and{' '}
          <a href="https://www.tiktok.com/legal/page/global/music-usage-confirmation" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:underline">
            Music Usage Confirmation
          </a>.
        </>
      );
    }
    return (
      <>
        By posting, you agree to TikTok&apos;s{' '}
        <a href="https://www.tiktok.com/legal/page/global/music-usage-confirmation" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:underline">
          Music Usage Confirmation
        </a>.
      </>
    );
  };

  const handlePost = async () => {
    if (!caption.trim()) {
      toast.error('Please enter a caption');
      return;
    }
    if (!privacyLevel) {
      toast.error('Please select who can view this video');
      return;
    }
    if (!isCommercialContentValid()) {
      toast.error('Please select at least one: "Your brand" or "Branded content"');
      return;
    }
    if (!userConsent) {
      toast.error('Please agree to the terms');
      return;
    }

    setPosting(true);
    
    try {
      const token = localStorage.getItem('token');
      
      // Validate token before making request
      if (!token) {
        toast.error('Session expired. Please log in again.');
        return;
      }
      
      console.log('TikTok post - sending request with token:', token ? `${token.slice(0, 20)}...` : 'NO TOKEN');
      
      const response = await axios.post(`${API}/api/social/post`, {
        platform: 'tiktok',
        content_id: content.id,
        text: caption,
        tiktok_settings: {
          privacy_level: privacyLevel,
          disable_comment: !allowComment,
          disable_duet: !allowDuet,
          disable_stitch: !allowStitch,
          brand_content_toggle: discloseContent,
          brand_organic_toggle: isYourBrand,
          is_branded_content: isBrandedContent
        }
      }, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('TikTok post result:', response.data);
      
      if (response.data.success) {
        toast.success('Video uploaded! It may take a few minutes to appear on your profile.');
        onPostSuccess?.(response.data);
        onClose();
      } else {
        const errorMsg = response.data.error || response.data.detail || 'Failed to post to TikTok';
        console.error('TikTok post error:', errorMsg);
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error('Post error:', error);
      // Check for specific error types
      if (error.response?.status === 401) {
        toast.error('Session expired. Please log in again and try posting.');
      } else {
        const errorMsg = error.response?.data?.error || error.response?.data?.detail || error.message || 'Network error while posting to TikTok';
        toast.error(errorMsg);
      }
    } finally {
      setPosting(false);
    }
  };

  const getPrivacyIcon = (level) => {
    switch (level) {
      case 'PUBLIC_TO_EVERYONE': return <Globe className="w-4 h-4" />;
      case 'MUTUAL_FOLLOW_FRIENDS':
      case 'FOLLOWER_OF_CREATOR': return <Users className="w-4 h-4" />;
      case 'SELF_ONLY': return <Lock className="w-4 h-4" />;
      default: return <Globe className="w-4 h-4" />;
    }
  };

  const getPrivacyLabel = (level) => {
    switch (level) {
      case 'PUBLIC_TO_EVERYONE': return 'Public';
      case 'MUTUAL_FOLLOW_FRIENDS': return 'Friends';
      case 'FOLLOWER_OF_CREATOR': return 'Followers';
      case 'SELF_ONLY': return 'Only me';
      default: return level;
    }
  };

  const isVideo = content?.has_video || content?.video_base64;

  // POINT 3b: Check if "Only me" should be disabled
  const isPrivateDisabled = isBrandedContent;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-gray-900 border-gray-700 text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor">
              <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
            </svg>
            Upload to TikTok
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 py-4">
          {/* LOADING */}
          {loading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-pink-500" />
              <span className="ml-2">Loading your TikTok account info...</span>
            </div>
          )}

          {/* ERROR */}
          {creatorInfoError && !loading && (
            <div className="bg-red-500/20 border border-red-500 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-red-400">Unable to connect to TikTok</p>
                <p className="text-sm text-gray-300 mt-1">{creatorInfoError}</p>
                <Button variant="outline" size="sm" className="mt-2" onClick={fetchCreatorInfo}>
                  Try Again
                </Button>
              </div>
            </div>
          )}

          {/* MAIN CONTENT */}
          {creatorInfo && !loading && (
            <>
              {/* POINT 1a: Creator Info */}
              <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                <div className="flex items-center gap-3">
                  {creatorInfo.avatar_url ? (
                    <img src={creatorInfo.avatar_url} alt="Profile" className="w-12 h-12 rounded-full" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center">
                      <span className="text-lg font-bold">{creatorInfo.display_name?.[0] || 'T'}</span>
                    </div>
                  )}
                  <div className="flex-1">
                    <p className="font-semibold">{creatorInfo.display_name || 'TikTok User'}</p>
                    <p className="text-sm text-gray-400">@{creatorInfo.username || 'user'}</p>
                  </div>
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                </div>
              </div>

              {/* POINT 1b: Posting Limit */}
              {postingLimitMessage && (
                <div className="bg-yellow-500/20 border border-yellow-500 rounded-lg p-4 flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-yellow-500" />
                  <p className="text-yellow-400">{postingLimitMessage}</p>
                </div>
              )}

              {/* POINT 1c: Video Duration */}
              {videoDurationError && (
                <div className="bg-red-500/20 border border-red-500 rounded-lg p-4 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500" />
                  <p className="text-red-400">{videoDurationError}</p>
                </div>
              )}

              {canPost && (
                <>
                  {/* Preview + Caption */}
                  <div className="flex gap-4">
                    <div className="w-28 h-40 bg-gray-800 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0 border border-gray-700">
                      {isVideo && content?.video_base64 ? (
                        <video src={`data:video/mp4;base64,${content.video_base64}`} className="w-full h-full object-cover" />
                      ) : content?.image_base64 ? (
                        <img src={`data:image/png;base64,${content.image_base64}`} alt="Preview" className="w-full h-full object-cover" />
                      ) : (
                        <Play className="w-8 h-8 text-gray-500" />
                      )}
                    </div>
                    
                    {/* POINT 2a: Caption */}
                    <div className="flex-1 space-y-2">
                      <Label className="text-sm font-medium text-gray-300">Caption</Label>
                      <Textarea
                        value={caption}
                        onChange={(e) => setCaption(e.target.value)}
                        placeholder="Add a title that describes your video"
                        className="min-h-[100px] bg-gray-800 border-gray-700 text-white resize-none"
                        maxLength={2200}
                      />
                      <p className="text-xs text-gray-500 text-right">{caption.length}/2200</p>
                    </div>
                  </div>

                  {/* POINT 2b: Who can view - NO DEFAULT */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-300">Who can view this video</Label>
                    <Select 
                      value={privacyLevel} 
                      onValueChange={(value) => {
                        if (value === 'SELF_ONLY' && isBrandedContent) {
                          toast.error('Branded content visibility cannot be set to private.');
                          return;
                        }
                        setPrivacyLevel(value);
                      }}
                    >
                      <SelectTrigger className={`bg-gray-800 border-gray-700 ${!privacyLevel ? 'border-yellow-500' : ''}`}>
                        <SelectValue placeholder="Select...">
                          {privacyLevel && (
                            <div className="flex items-center gap-2">
                              {getPrivacyIcon(privacyLevel)}
                              {getPrivacyLabel(privacyLevel)}
                            </div>
                          )}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-700">
                        {(creatorInfo?.privacy_level_options || ['PUBLIC_TO_EVERYONE', 'MUTUAL_FOLLOW_FRIENDS', 'FOLLOWER_OF_CREATOR', 'SELF_ONLY']).map((option) => (
                          <SelectItem 
                            key={option} 
                            value={option} 
                            className="text-white hover:bg-gray-700"
                            disabled={option === 'SELF_ONLY' && isPrivateDisabled}
                          >
                            <div className="flex items-center gap-2">
                              {getPrivacyIcon(option)}
                              {getPrivacyLabel(option)}
                              {option === 'SELF_ONLY' && isPrivateDisabled && (
                                <span className="text-xs text-red-400 ml-2">(Branded content cannot be private)</span>
                              )}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* POINT 2c: Allow users to - ALL UNCHECKED */}
                  <div className="space-y-3">
                    <Label className="text-sm font-medium text-gray-300">Allow users to</Label>
                    
                    <div className="flex items-center gap-3">
                      <Checkbox
                        id="allow-comment"
                        checked={allowComment}
                        onCheckedChange={setAllowComment}
                        disabled={commentDisabledByCreator}
                        className="border-gray-500 data-[state=checked]:bg-cyan-500"
                      />
                      <label htmlFor="allow-comment" className={`text-sm ${commentDisabledByCreator ? 'text-gray-500' : 'text-white'}`}>
                        Comment {commentDisabledByCreator && <span className="text-xs text-gray-500">(Disabled in settings)</span>}
                      </label>
                    </div>

                    {isVideo && (
                      <>
                        <div className="flex items-center gap-3">
                          <Checkbox
                            id="allow-duet"
                            checked={allowDuet}
                            onCheckedChange={setAllowDuet}
                            disabled={duetDisabledByCreator}
                            className="border-gray-500 data-[state=checked]:bg-cyan-500"
                          />
                          <label htmlFor="allow-duet" className={`text-sm ${duetDisabledByCreator ? 'text-gray-500' : 'text-white'}`}>
                            Duet {duetDisabledByCreator && <span className="text-xs text-gray-500">(Disabled in settings)</span>}
                          </label>
                        </div>

                        <div className="flex items-center gap-3">
                          <Checkbox
                            id="allow-stitch"
                            checked={allowStitch}
                            onCheckedChange={setAllowStitch}
                            disabled={stitchDisabledByCreator}
                            className="border-gray-500 data-[state=checked]:bg-cyan-500"
                          />
                          <label htmlFor="allow-stitch" className={`text-sm ${stitchDisabledByCreator ? 'text-gray-500' : 'text-white'}`}>
                            Stitch {stitchDisabledByCreator && <span className="text-xs text-gray-500">(Disabled in settings)</span>}
                          </label>
                        </div>
                      </>
                    )}
                  </div>

                  {/* POINT 3: Disclose video content */}
                  <div className="space-y-3 bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <Label className="text-sm font-medium text-gray-300">Disclose video content</Label>
                        <p className="text-xs text-gray-500 mt-1">
                          Turn on to disclose that this video promotes goods or services in exchange for something of value. Your video could promote yourself, a third party, or both.
                        </p>
                      </div>
                      <Switch
                        checked={discloseContent}
                        onCheckedChange={(checked) => {
                          setDiscloseContent(checked);
                          if (!checked) {
                            setIsYourBrand(false);
                            setIsBrandedContent(false);
                          }
                        }}
                      />
                    </div>

                    {discloseContent && (
                      <div className="space-y-3 mt-3 pt-3 border-t border-gray-700">
                        
                        {/* POINT 3a: Show ONLY ONE prompt based on selection */}
                        {getDisclosurePrompt() && (
                          <div className="bg-cyan-500/20 border border-cyan-500/50 rounded-lg p-3 flex items-start gap-2">
                            <Info className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                            <p className="text-sm text-cyan-300">{getDisclosurePrompt()}</p>
                          </div>
                        )}

                        {/* Your Brand checkbox */}
                        <div className="flex items-start gap-3">
                          <Checkbox
                            id="your-brand"
                            checked={isYourBrand}
                            onCheckedChange={setIsYourBrand}
                            className="border-gray-500 data-[state=checked]:bg-cyan-500 mt-0.5"
                          />
                          <div className="flex-1">
                            <label htmlFor="your-brand" className="text-sm font-medium text-white cursor-pointer">
                              Your brand
                            </label>
                            <p className="text-xs text-gray-400 mt-0.5">
                              You are promoting yourself or your own business. This video will be classified as Brand Organic.
                            </p>
                          </div>
                        </div>

                        {/* Branded Content checkbox */}
                        <div className="flex items-start gap-3">
                          <Checkbox
                            id="branded-content"
                            checked={isBrandedContent}
                            onCheckedChange={(checked) => {
                              setIsBrandedContent(checked);
                              // POINT 3b: If enabling branded content and privacy is "Only me", reset privacy
                              if (checked && privacyLevel === 'SELF_ONLY') {
                                setPrivacyLevel('PUBLIC_TO_EVERYONE');
                                toast.info('Privacy changed to Public. Branded content visibility cannot be set to private.');
                              }
                            }}
                            className="border-gray-500 data-[state=checked]:bg-cyan-500 mt-0.5"
                          />
                          <div className="flex-1">
                            <label htmlFor="branded-content" className="text-sm font-medium text-white cursor-pointer">
                              Branded content
                            </label>
                            <p className="text-xs text-gray-400 mt-0.5">
                              You are promoting another brand or a third party. This video will be classified as Branded Content.
                            </p>
                          </div>
                        </div>

                        {/* Validation message when toggle ON but nothing selected */}
                        {discloseContent && !isYourBrand && !isBrandedContent && (
                          <div 
                            className="text-xs text-yellow-500 flex items-center gap-1 cursor-help"
                            title="You need to indicate if your content promotes yourself, a third party, or both."
                          >
                            <AlertTriangle className="w-3 h-3" />
                            You need to indicate if your content promotes yourself, a third party, or both.
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* POINT 4: Consent */}
                  <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="consent-checkbox"
                        checked={userConsent}
                        onCheckedChange={setUserConsent}
                        className="border-gray-500 data-[state=checked]:bg-cyan-500 mt-0.5"
                      />
                      <label htmlFor="consent-checkbox" className="text-sm text-gray-300 cursor-pointer">
                        {getConsentText()}
                      </label>
                    </div>
                  </div>

                  {/* POINT 5d: Processing notice */}
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    <Info className="w-3 h-3" />
                    After publishing, it may take a few minutes for the content to process and be visible on your profile.
                  </p>
                </>
              )}
            </>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="border-gray-600 text-gray-300">
            Cancel
          </Button>
          <Button
            onClick={handlePost}
            disabled={
              posting || 
              loading || 
              creatorInfoError || 
              !creatorInfo || 
              !canPost || 
              !privacyLevel || 
              !userConsent ||
              (discloseContent && !isYourBrand && !isBrandedContent)
            }
            className="bg-[#fe2c55] hover:bg-[#ef2c50] disabled:opacity-50"
          >
            {posting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              'Upload'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
